/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1691971200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Aug 14 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "materialize",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Provide credentials",
          "depth": 1,
          "id": "provide-credentials"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Canceling statement due to statement timeout",
          "depth": 1,
          "id": "canceling-statement-due-to-statement-timeout"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      pre: "pre",
      code: "code",
      span: "span"
    }, _provideComponents(), props.components), {SourceTestSnippet, SyncLogsSnippet, CommonErrorsPreface} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!SourceTestSnippet) _missingMdxReference("SourceTestSnippet", true);
    if (!SyncLogsSnippet) _missingMdxReference("SyncLogsSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you pull data stored in your Materialize database and push it to downstream destinations. Most of the setup occurs in the Hightouch UI, but you need access to your Materialize instance for information like your host, port, database name, cluster, and credentials."
      }), "\n", _jsx(WhitelistDetails, {}), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources?from=docs",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Materialize"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your Materialize server."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your Materialize server. The default port number is 6875, but yours may be different."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), ": This specifies the database to use when Hightouch executes queries in Materialize."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "provide-credentials",
        children: "Provide credentials"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User"
          }), ": This can be your personal Materialize login or a dedicated user for Hightouch. At minimum, this user must have read access to the data you wish to sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": The password for the user specified above."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsx(SourceTestSnippet, {
        source: "Materialize"
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Materialize."]
      }), "\n", _jsx(_components.p, {
        children: "The Materialize source supports these modeling methods:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["writing a query in ", _jsx(_components.a, {
            href: "/models/sql-editor",
            children: "the SQL editor"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["using the visual ", _jsx(_components.a, {
            href: "/models/table-selector",
            children: "table selector"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/dbt-models",
            children: "dbt models"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/looker-models",
            children: "Looker Looks"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/sigma",
            children: "Sigma workbooks"
          })]
        }), "\n"]
      }), "\n", _jsx(SyncLogsSnippet, {
        source: "Materialize"
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "canceling-statement-due-to-statement-timeout",
        children: "Canceling statement due to statement timeout"
      }), "\n", _jsx(_components.p, {
        children: "This error occurs when the required execution time of your Materialize query exceeds the timeout limit for the database.\nTo address the error, increase the timeout by executing the following query:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: ["set statement_timeout = '", _jsx(_components.span, {
            className: "hljs-number",
            children: "300"
          }), " s'; -- ", _jsx(_components.span, {
            className: "hljs-number",
            children: "300"
          }), " seconds", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "5"
          }), " minutes\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Be sure to adjust the time to be as long as it takes for your query to execute."
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
